import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-step phone change modal:
 *   Step 1 (`enter`)  — phone input + Send code
 *   Step 2 (`verify`) — 6-digit OTP input + Confirm
 *
 * In dev (no SMS_PROVIDER env set) the backend returns the OTP via `dev_code`
 * which we surface as a small hint. On a production deployment with a real
 * provider, that field is absent and the user reads the code from their SMS.
 */
export const PhoneVerifyModal = ({ open, currentPhone, onClose, onVerified }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('enter');
  const [phone, setPhone] = useState(currentPhone || '');
  const [code, setCode] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep('enter');
    setPhone(currentPhone || '');
    setCode('');
    setNormalizedPhone('');
    setDevCode(null);
    setBusy(false);
  };

  const close = () => { reset(); onClose(); };

  const sendCode = async () => {
    if (!phone || phone.trim().length < 8) {
      toast.error(t('settings.phoneInvalid') || 'Invalid phone number');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/profile/phone/start`,
        { phone: phone.trim() },
        { withCredentials: true },
      );
      setNormalizedPhone(data.phone);
      setDevCode(data.dev_code || null);
      setStep('verify');
      toast.success(t('settings.codeSent') || 'Verification code sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('settings.codeFailed') || 'Failed to send code'));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      toast.error(t('settings.codeInvalid') || 'Enter the 6-digit code');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/profile/phone/verify`,
        { phone: normalizedPhone, code },
        { withCredentials: true },
      );
      toast.success(t('settings.phoneVerified') || 'Phone verified');
      onVerified(data.phone);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || (t('settings.codeWrong') || 'Invalid code'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-sm" data-testid="phone-verify-modal">
        <DialogHeader>
          <DialogTitle>
            {step === 'enter'
              ? (t('settings.changePhone') || 'Change phone number')
              : (t('settings.verifyPhone') || 'Verify phone number')}
          </DialogTitle>
        </DialogHeader>

        {step === 'enter' ? (
          <div className="space-y-3">
            <div>
              <Label>{t('auth.phone')}</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+261 34 12 345 67"
                inputMode="tel"
                className="border border-border"
                data-testid="phone-verify-input"
              />
              <p className="text-xs text-text_muted mt-1">
                {t('settings.phoneOtpHint') || 'We will send you a 6-digit verification code.'}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="phone-verify-cancel">
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={sendCode}
                disabled={busy}
                variant="primary"
                data-testid="phone-verify-send-code"
              >
                {busy ? (t('common.loading') || 'Loading...') : (t('settings.sendCode') || 'Send code')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text_muted">
              {(t('settings.codeSentTo') || 'Code sent to')} <span className="font-medium text-text_main">{normalizedPhone}</span>
            </p>
            <div>
              <Label>{t('settings.verificationCode') || 'Verification code'}</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                maxLength={6}
                className="border border-border tracking-widest text-center text-lg"
                data-testid="phone-verify-code-input"
              />
              {devCode && (
                <p className="text-xs text-warning bg-warning-soft border border-warning rounded-md px-2 py-1 mt-2" data-testid="phone-verify-dev-code">
                  Dev mode — code: <span className="font-mono font-semibold">{devCode}</span>
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('enter')} disabled={busy} data-testid="phone-verify-back">
                {t('common.back') || 'Back'}
              </Button>
              <Button
                onClick={verifyCode}
                disabled={busy || code.length !== 6}
                variant="primary"
                data-testid="phone-verify-confirm"
              >
                {busy ? (t('common.loading') || 'Loading...') : (t('settings.confirm') || 'Confirm')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
